import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Search as SearchIcon, Leaf, FlaskConical, AlertTriangle, ArrowRight, ShieldCheck } from 'lucide-react';
import { essentialOils, carrierOils, herbs, recipes } from '../data/mock';
import { useProfiles } from '../contexts/ProfileContext';

const allLibrary = [
  ...essentialOils.map((i) => ({ ...i, category: 'Essential Oil' })),
  ...carrierOils.map((i) => ({ ...i, category: 'Carrier Oil' })),
  ...herbs.map((i) => ({ ...i, category: 'Herb' }))
];

const popular = ['sleep', 'anxiety', 'cold', 'headache', 'skin', 'digestion', 'immune', 'stress'];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeProfile, filterLibrary, filterRecipes } = useProfiles();
  const initial = params.get('q') || '';
  const [query, setQuery] = useState(initial);

  useEffect(() => {
    setQuery(params.get('q') || '');
  }, [params]);

  const q = (params.get('q') || '').trim().toLowerCase();

  const libResults = useMemo(() => {
    if (!q) return [];
    const base = allLibrary.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        i.latin.toLowerCase().includes(q) ||
        i.benefits.join(' ').toLowerCase().includes(q) ||
        i.properties.join(' ').toLowerCase().includes(q)
    );
    return filterLibrary(base);
  }, [q, filterLibrary]);

  const recipeResults = useMemo(() => {
    if (!q) return [];
    const base = recipes.filter(
      (r) => r.name.toLowerCase().includes(q) || r.symptoms.join(' ').toLowerCase().includes(q) || r.type.toLowerCase().includes(q)
    );
    return filterRecipes(base);
  }, [q, filterRecipes]);

  const submit = (e) => {
    e.preventDefault();
    setParams(query.trim() ? { q: query.trim() } : {});
  };

  const total = libResults.length + recipeResults.length;

  return (
    <div className="max-w-5xl mx-auto px-5 py-10 min-h-[70vh]">
      <h1 className="font-serif text-4xl text-botanica-ink mb-2">Find a remedy</h1>
      <p className="text-botanica-muted mb-6">Search by symptom, herb, oil or recipe.</p>

      <form onSubmit={submit} className="relative mb-4">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-botanica-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. trouble sleeping, sore throat, lavender..."
          className="w-full pl-11 pr-28 py-3.5 rounded-2xl bg-botanica-card/70 border border-botanica-card text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
        />
        <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-full bg-botanica-green text-white text-sm font-medium hover:bg-botanica-greenDark transition-colors">
          Search
        </button>
      </form>

      <div className="flex flex-wrap gap-2 mb-8">
        {popular.map((p) => (
          <button
            key={p}
            onClick={() => setParams({ q: p })}
            className="text-xs px-3 py-1.5 rounded-full bg-botanica-greenLight text-botanica-greenDark hover:bg-botanica-green hover:text-white transition-colors capitalize"
          >
            {p}
          </button>
        ))}
      </div>

      {!q && <p className="text-sm text-botanica-muted">Try a symptom above, or type what you're feeling.</p>}

      {q && (
        <p className="text-sm text-botanica-muted mb-6">
          {total} result{total === 1 ? '' : 's'} for “{q}”
          {activeProfile && (
            <span className="ml-2 inline-flex items-center gap-1 text-botanica-greenDark">
              <ShieldCheck className="w-3.5 h-3.5" /> filtered for {activeProfile.first_name}
            </span>
          )}
        </p>
      )}

      {q && recipeResults.length > 0 && (
        <section className="mb-10">
          <h2 className="font-serif text-2xl text-botanica-ink mb-4 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-botanica-green" /> Recipes
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recipeResults.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/recipes/${r.id}`)}
                className="text-left bg-botanica-card/70 hover:bg-botanica-card rounded-2xl p-5 transition-all hover:-translate-y-0.5"
              >
                <h3 className="font-serif text-lg text-botanica-ink leading-tight">{r.name}</h3>
                <p className="text-xs text-botanica-muted mt-1">{r.type} · {r.time} · {r.difficulty}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {r.symptoms.map((s) => (
                    <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-botanica-greenLight text-botanica-greenDark">{s}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {q && libResults.length > 0 && (
        <section className="mb-10">
          <h2 className="font-serif text-2xl text-botanica-ink mb-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-botanica-green" /> Herbs &amp; oils
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {libResults.map((i) => (
              <button
                key={i.id}
                onClick={() => navigate(`/library/${i.id}`)}
                className="text-left bg-botanica-card/70 hover:bg-botanica-card rounded-2xl p-5 transition-all hover:-translate-y-0.5"
              >
                <p className="text-[10px] uppercase tracking-widest text-botanica-green font-semibold">{i.category}</p>
                <h3 className="font-serif text-lg text-botanica-ink leading-tight">{i.name}</h3>
                <p className="italic text-xs text-botanica-muted">{i.latin}</p>
                <p className="text-sm text-botanica-ink/80 mt-2">{i.benefits.join(' • ')}</p>
                <p className={`text-xs mt-2 flex items-center gap-1.5 ${i.level === 'caution' ? 'text-botanica-warn' : 'text-botanica-green'}`}>
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {i.safety}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}

      {q && total === 0 && (
        <div className="text-center py-16">
          <p className="text-botanica-muted mb-4">No matches for “{q}”. Try a broader term or ask Sage.</p>
          <Link to="/ask" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-botanica-green text-white text-sm font-medium hover:bg-botanica-greenDark transition-colors">
            Ask Sage <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
